import { useRef, useEffect, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";

const WELCOME = {
  role: "assistant",
  text: "Ciao, sono l'assistente di AB fëlise shop. Posso aiutarti con prodotti, spedizioni, resi e ordini — come posso esserti utile?",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, history: next.slice(-11, -1) });
      setMessages([...next, { role: "assistant", text: data.text }]);
    } catch {
      setMessages([
        ...next,
        { role: "assistant", text: "Mi dispiace, l'assistente non è disponibile in questo momento. Scrivici a info@abfeliseshop.it." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 md:right-8 z-[70] w-[calc(100vw-40px)] max-w-sm h-[480px] border border-white/10 bg-[#121214] flex flex-col shadow-2xl"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-primary">AB fëlise</p>
                <p className="text-sm text-muted-foreground">Assistente clienti</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Chiudi chat"
              >
                <X size={17} />
              </button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`text-sm leading-relaxed max-w-[85%] px-4 py-3 ${
                    m.role === "user"
                      ? "ml-auto bg-primary text-primary-foreground"
                      : "mr-auto bg-secondary text-foreground"
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: m.text
                      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                      .replace(/\n/g, "<br/>"),
                  }}
                />
              ))}
              {busy && (
                <p className="text-xs text-muted-foreground animate-pulse" data-testid="chat-typing">
                  Sta scrivendo…
                </p>
              )}
            </div>
            <form onSubmit={send} className="flex gap-2 p-4 border-t border-white/10">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder="Scrivi un messaggio…"
                className="flex-1 bg-secondary border border-white/10 px-4 py-3 text-sm outline-none focus:border-primary/50 placeholder:text-muted-foreground/60"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="chat-send"
                className="h-11 w-11 flex-shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-[#c8ad8f] transition-colors disabled:opacity-40"
                aria-label="Invia messaggio"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 200 }}
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-button"
        className="fixed bottom-6 right-5 md:right-8 z-[70] h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-[#c8ad8f] transition-colors duration-300 shadow-lg"
        aria-label="Apri assistenza"
      >
        <MessageCircle size={20} strokeWidth={1.5} />
      </motion.button>
    </>
  );
}
